import type { TeacherDashboardPerformance, TeacherDashboardPerformanceRun } from "../../api/teacherWeb";
import type { StandardnessDimension, StandardnessScore } from "../result/standardness";
import { actionKey, beijingDate } from "./dashboardModel";

export const dimensions: Array<{ key: StandardnessDimension; label: string }> = [
  { key: "start", label: "启动" }, { key: "impact", label: "击球" },
  { key: "followThrough", label: "随挥" }, { key: "power", label: "力量" },
  { key: "stability", label: "稳定" }, { key: "technique", label: "技巧" },
];
export const scoreText = (value: number | null | undefined) => value == null ? "—" : value.toFixed(1);
export const performanceKey = (run: TeacherDashboardPerformanceRun) => `${run.studentUserId}:${actionKey(run.techType ?? "unknown")}`;
export function filterPerformance(data: TeacherDashboardPerformance | undefined, techType: string, days: number) {
  if (!data) return undefined;
  const end = beijingDate(data.endAt).getTime();
  const start = days ? Math.floor((end + 8 * 3600000) / 86400000) * 86400000 - 8 * 3600000 - (days - 1) * 86400000 : beijingDate(data.startAt).getTime();
  return { ...data, techType, days, startAt: new Date(start).toISOString(), runs: data.runs.filter(run =>
    (techType === "all" || actionKey(run.techType ?? data.techType) === actionKey(techType)) && beijingDate(run.createdAt).getTime() >= start && beijingDate(run.createdAt).getTime() <= end) };
}
export function chronological(runs: TeacherDashboardPerformanceRun[]) {
  return [...runs].sort((a, b) => beijingDate(b.createdAt).getTime() - beijingDate(a.createdAt).getTime());
}
export function scoreChange(runs: TeacherDashboardPerformanceRun[]) {
  const ordered = chronological(runs);
  const latest = ordered[0]?.score?.value;
  const previous = ordered[1]?.score?.value;
  return latest == null || previous == null ? null : latest - previous;
}
export function summarizePerformance(runs: TeacherDashboardPerformanceRun[]) {
  // One latest completed attempt per student/action, even across classes. Never silently
  // substitute an older score when their newest payload cannot be scored.
  const latest = new Map<string, TeacherDashboardPerformanceRun>();
  chronological(runs).forEach(run => { const key = performanceKey(run); if (!latest.has(key)) latest.set(key, run); });
  const scores = [...latest.values()].flatMap(run => run.score ? [run.score] : []);
  const values = scores.map(score => score.value).sort((a, b) => a - b);
  const middle = Math.floor(values.length / 2);
  const median = values.length ? (values.length % 2 ? values[middle] : (values[middle - 1] + values[middle]) / 2) : null;
  const axes = dimensions.map(axis => {
    const available = scores.flatMap(score => {
      const value = score.dimensions.find(d => d.dimension === axis.key)?.value;
      return value == null ? [] : [value];
    });
    return { ...axis, count: available.length, value: available.length ? available.reduce((a, b) => a + b, 0) / available.length : null };
  });
  const bins = Array.from({ length: 6 }, (_, i) => ({ from: 40 + i * 10, to: 50 + i * 10, count: 0 }));
  values.forEach(value => { bins[Math.min(5, Math.max(0, Math.floor((value - 40) / 10)))].count++; });
  const mean = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  const radar: StandardnessScore | null = mean == null ? null : { value: mean, displayValue: Math.round(mean), dimensions: axes.map(axis => ({ dimension: axis.key, value: axis.value })) };
  return { latest, count: values.length, studentCount: new Set([...latest.values()].filter(run => run.score).map(run => run.studentUserId)).size, missing: latest.size - values.length, mean, median, axes, bins, radar };
}
